import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import config from "../../../config/config";
import { getToken } from "../../../utils/localStorageMethods";

const apiUrl = config.Url;

const baseQuery = fetchBaseQuery({
  baseUrl: `${apiUrl}/subscription-payment/`,
  credentials: "include",
  prepareHeaders: (headers) => {
    const token = getToken();
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
      headers.set("ngrok-skip-browser-warning", true);
    }
    return headers;
  },
});

export const paymentHistorySlice = createApi({
  reducerPath: "paymentHistorySlice",
  baseQuery: baseQuery,
  tagTypes: ["payment"],
  endpoints: (builder) => ({
    // School / Vendor own history  |  Parent own history (branched on backend by role)
    getPaymentHistory: builder.query({
      query: ({ page, limit }) => {
        const params = new URLSearchParams();
        if (page) params.append("page", page);
        if (limit) params.append("limit", limit);
        return `history?${params.toString()}`;
      },
      providesTags: ["payment"],
    }),

    // Admin: all payment histories filtered by role (school | vendor | parent)
    getAdminPaymentHistory: builder.query({
      query: ({ page, limit, role }) => {
        const params = new URLSearchParams();
        if (page) params.append("page", page);
        if (limit) params.append("limit", limit);
        if (role) params.append("role", role);
        return `admin-history?${params.toString()}`;
      },
      providesTags: ["payment"],
    }),

    // School/Vendor: parent payments received
    getParentPaymentsReceived: builder.query({
      query: ({ page, limit }) => {
        const params = new URLSearchParams();
        if (page) params.append("page", page);
        if (limit) params.append("limit", limit);
        return `parent-received-history?${params.toString()}`;
      },
      providesTags: ["payment"],
    }),

    // Parent payment summary detail (school/vendor/admin)
    getParentPaymentSummaryDetail: builder.query({
      query: (summaryId) => `parent-summary-detail/${summaryId}`,
      providesTags: ["payment"],
    }),

    // Retry a "Created" (unconfirmed) payment — returns a new iframe_url
    retryPayment: builder.mutation({
      query: (orderId) => ({
        url: `retry`,
        method: "POST",
        body: { orderId },
      }),
      invalidatesTags: ["payment"],
    }),

    deleteParentPayment: builder.mutation({
      query: (id) => ({ url: `history/${id}`, method: "DELETE" }),
      invalidatesTags: ["payment"],
    }),

    deleteSubscriptionHistory: builder.mutation({
      query: (id) => ({ url: `subscription-history/${id}`, method: "DELETE" }),
      invalidatesTags: ["payment"],
    }),

    // Parent manually subscribes for the next academic year right after
    // the current one ends, instead of waiting for the Sep 1 auto-renewal.
    subscribeNow: builder.mutation({
      query: () => ({ url: `subscribe-now`, method: "POST" }),
      invalidatesTags: ["payment"],
    }),

    // Parent refreshes their saved card via a $1 verify-then-void checkout.
    initiateCardUpdate: builder.mutation({
      query: () => ({ url: `update-payment-method`, method: "POST" }),
    }),

    // Parent who previously cancelled starts a brand new subscription cycle.
    resubscribe: builder.mutation({
      query: () => ({ url: `resubscribe`, method: "POST" }),
      invalidatesTags: ["payment"],
    }),
  }),
});

export const {
  useGetPaymentHistoryQuery,
  useGetAdminPaymentHistoryQuery,
  useGetParentPaymentsReceivedQuery,
  useGetParentPaymentSummaryDetailQuery,
  useRetryPaymentMutation,
  useDeleteParentPaymentMutation,
  useDeleteSubscriptionHistoryMutation,
  useSubscribeNowMutation,
  useInitiateCardUpdateMutation,
  useResubscribeMutation,
} = paymentHistorySlice;
